// add js methods that doesn't exist in older browsers
import "core-js";
import DOMPurify from "dompurify";
import "intersection-observer";
import { action, observable } from "mobx";
import { Observer } from "mobx-react";
import React from "react";
import ReactDOM from "react-dom";
import "regenerator-runtime/runtime";
import "whatwg-fetch";
import { CvpSettings } from "../api/settings/CvpSettings";
import { GetSettingsResponse } from "../api/settings/getSettings";
import { getNextApiUrl } from "../api/getNextApiUrl";
import { AppType } from "../WSUtils/AppType";
import {
  PostMessageCVPEmbedConfig,
  PostMessageCVPEmbedReadyForSettings,
  PostMessageCVPReload,
  PostMessageCVPResizeIframe,
  PostMessageTypes,
} from "../WSUtils/PostMessageTypes";
import { routerStore, setupRouterStore } from "../WSUtils/WSRouter";
import { getHostOrigin } from "../WSUtils/getHostOrigin";
import { CvpLocale } from "../WSUtils/getIdLanguage";
import { lp } from "../WSUtils/logPrefix";
import { guid } from "../WSUtils/util";
import { TRANSLATION_PREVIEW_PREFIX } from "../wsLocalization/translationPreviewPrefix";
import { App } from "./App";
import { mapCvpSettingsFromServer } from "../api/settings/mapCvpSettingsFromServer";

export function startApp() {
  getHostOrigin();

  setupRouterStore();

  const settings = observable.box<CvpSettings>(null);

  // remove scrips etc, since some of the settings end up in the DOM
  const sanitizeString = (s: string) => {
    if (s === null || s === undefined) return s;

    return DOMPurify.sanitize(s);
  };

  const sanitizeSettings = (settings: CvpSettings): CvpSettings => {
    // UPDATE CODE HERE WHEN ADDING NEW SETTING (2)
    const sanitized: CvpSettings = {
      idConcern: sanitizeString(settings.idConcern),
      idEmbed: sanitizeString(settings.idEmbed),
      backgroundColor: sanitizeString(settings.backgroundColor),
      cssUrl: sanitizeString(settings.cssUrl),
      css: sanitizeString(settings.css),
      fontFamily: sanitizeString(settings.fontFamily),
      fontSize: sanitizeString(settings.fontSize),
      locale: sanitizeString(settings.locale) as CvpLocale,
      primaryColor: sanitizeString(settings.primaryColor),
      startPage: sanitizeString(settings.startPage),
      theme: sanitizeString(settings.theme),
      departments: sanitizeString(settings.departments),
      logoPrimary: settings.logoPrimary, // why is this one not sanitized?
      logoJobFallback: settings.logoJobFallback, // why is this one not sanitized?
      iframe: settings.iframe,
      iframe_joblist_showLoginButton: settings.iframe_joblist_showLoginButton,
      jobview_showContactImage: settings.jobview_showContactImage,
    };

    Object.entries(settings).forEach(([key, value]) => {
      if (key.startsWith(TRANSLATION_PREVIEW_PREFIX)) {
        sanitized[key] = sanitizeString(value);
      }
    });

    Object.keys(sanitized).forEach((key) => {
      if (settings[key] !== sanitized[key]) {
        console.error(lp("Sanitation of a setting caused it to change"), {
          key,
          setting: settings[key],
          sanitized: sanitized[key],
        });
      }
    });

    return sanitized;
  };

  let appStarted = false;
  function renderApp() {
    if (appStarted) return;
    appStarted = true;

    ReactDOM.render(
      <Observer render={() => <App settings={settings} />} />,
      document.getElementById(settings.get().idEmbed) ??
        document.getElementById("cvpreactroot")
    );
  }

  const loadId = guid();

  var startupSearchParams = new URL(window.location.href).searchParams;

  function startAppEmbedded() {
    if (!window.cvpSandboxType) {
      let st: any = startupSearchParams.get("cvpSandboxType");

      window.cvpSandboxType = st || null;
    }

    const handleCVPEmbedConfig = action((evt: MessageEvent<any>) => {
      const alreadyReceivedSettings = !!settings.get();

      const msg: PostMessageCVPEmbedConfig = evt.data;
      console.log(lp("receiveMessage"), msg);

      window.cvpEmbedType = msg.settings.iframe ? "iframe" : "script";

      settings.set(sanitizeSettings(msg.settings));
      routerStore.hostUrl = msg.hostUrl;

      // i think this can be deleted
      /*const isAlreadyViewingStartPage = !!location.href.endsWith(
        msg.settings.startPage
      );

      const shouldRedirect =
        window.cvpEmbedType === "iframe" &&
        !!msg.settings.startPage &&
        !isAlreadyViewingStartPage;

      if (shouldRedirect) {
        redirect(msg.settings.startPage, {
          method: "replace",
          userInfo: null,
        });
      }*/

      if (msg.sandbox) {
        const enableResizer = true;

        if (enableResizer) {
          const _msg: PostMessageCVPResizeIframe = {
            type: PostMessageTypes.CVPResizeIframe,
            idEmbed: msg.settings.idEmbed,
          };

          window.parent.postMessage(_msg, evt.origin);
          console.log(lp("window.parent.postMessage"), _msg);
        }

        if (alreadyReceivedSettings && !enableResizer) {
          const _msg: PostMessageCVPReload = {
            type: PostMessageTypes.CVPReload,
          };

          window.parent.postMessage(_msg, evt.origin);
          console.log(lp("window.parent.postMessage"), _msg);
        }
      }

      renderApp();
    });

    const allowOrigin = (senderOrigin: string) => {
      if (senderOrigin === window.location.origin) return true;
      if (senderOrigin === "https://cv.webtemp.no") return true;
      if (senderOrigin === "https://utvikling-secure.webtemp.no") return true;
      if (senderOrigin === "https://secure.webtemp.no") return true;
      if (senderOrigin === getHostOrigin()) return true;

      return false;
    };

    window.addEventListener("message", (evt) => {
      if (evt.data?.type !== PostMessageTypes.CVPEmbedConfig) return;

      if (!allowOrigin(evt.origin)) {
        console.warn(lp("Received message but origin not allowed"), {
          evt_origin: evt.origin,
          location_origin: window.location.origin,
          host_origin: getHostOrigin(),
          data: evt.data,
        });
        return;
      }

      handleCVPEmbedConfig(evt);
    });

    // tell host that iframe is ready to receive settings
    const msg: PostMessageCVPEmbedReadyForSettings = {
      type: PostMessageTypes.CVPEmbedReadyForSettings,
      loadId,
    };

    try {
      if (window.cvpEmbedType === "script") {
        window.postMessage(msg, window.location.origin);
        console.log(lp("window.postMessage"), msg);
      } else {
        window.parent.postMessage(msg, "*");
        console.log(lp("window.parent.postMessage"), msg);
      }
    } catch (e) {
      console.error(lp(), e);
    }
  }

  if (
    window.cvpEmbedType ||
    startupSearchParams.get("cvpEmbedType") ||
    window.cvpSandboxType
  ) {
    startAppEmbedded();
  } else {
    function getSettingsUrl() {
      return (
        getNextApiUrl("/cvp/CvpGetSettings") +
        `?verifyIdConcernWithAppTenant=${window.cvpIdConcern}` +
        `&idEmbed=${AppType.cvWebtempNo}`
      );
    }

    const url = getSettingsUrl();

    const settingsPromise: Promise<GetSettingsResponse> = fetch(url, {
      headers: {
        "content-type": "application/json",
        idConcern: window.cvpIdConcern.toString(),
      },
    })
      .then((r) => r.json())
      .catch((err) => {
        console.error(lp(), err);
        document.body.insertAdjacentText(
          "afterend",
          "Failed to load settings from " + url
        );
      });

    settingsPromise.then((response) => {
      if (!response.ok) {
        document.body.insertAdjacentText(
          "afterend",
          `Failed to load settings: ${response.message}`
        );
        return;
      }

      var cvpSettings = mapCvpSettingsFromServer(response);

      settings.set(sanitizeSettings(cvpSettings));
      renderApp();
    });
  }
}
